import { useState } from "react";

import type { ClaimRegisterResponse } from "@tm/insurance/api";
import { theme } from "@tm/utility/global";

import Spinner from "../../../../components/Spinner/Spinner";
import { ChevronUpIcon } from "../../../../icons";
import { useClaims } from "../../ClaimsContext";
import { useClaimBenefitDetails } from "../../hooks/useClaimBenefitDetails";
import type {
  AddDetailsClaimFormCardData,
  ClaimFlowData,
  ClaimFormViewProps,
  ClaimRequestCardData,
} from "../../types";
import { CLAIM_FLOW_CARD } from "../../types";
import { ClaimForm } from "../ClaimForm";
import { ClaimRequestSummary } from "../ClaimRequestSummary";
import { ClaimSubmitResult } from "../ClaimSubmitResult";
import {
  BackButton,
  BackIconWrapper,
  ErrorContainer,
  ErrorText,
  FormContainer,
  FormGrid,
  FormPage,
  LeftColumn,
  LoadingContainer,
  RetryButton,
  RightColumn,
} from "./ClaimFormView.style";

function findFlowCard<T>(
  flow: ClaimFlowData | undefined,
  cardName: string
): T | undefined {
  return flow?.cardCategory?.find((c) => c?.cardName === cardName)?.data as
    | T
    | undefined;
}

export function ClaimFormView({
  params,
  bookingId,
  partnerBookingId,
  currency,
  onBack,
}: ClaimFormViewProps) {
  const { fetchState, data, errorMessage, retry } = useClaimBenefitDetails({
    bookingId,
    partnerBookingId,
    currency,
    detailFetchParams: params?.detailFetchParams,
    contentType: params?.contentType,
  });

  const { refreshClaimData } = useClaims();
  const [result, setResult] = useState<ClaimRegisterResponse | null>(null);

  const flowData = data?.claimFlowData as ClaimFlowData | undefined;
  const formCard = findFlowCard<AddDetailsClaimFormCardData>(
    flowData,
    CLAIM_FLOW_CARD.ADD_DETAILS_FORM
  );

  if (result) {
    const onDone =
      result.registrationStatus === "SUCCESS" ? refreshClaimData : onBack;
    return (
      <ClaimSubmitResult
        response={result}
        onDone={onDone}
        onRetry={() => setResult(null)}
      />
    );
  }

  if (fetchState === "loading") {
    return (
      <FormPage>
        <LoadingContainer>
          <Spinner
            spinnerWidth={32}
            spinnerColor={theme.blueText}
            thickness={3}
          />
        </LoadingContainer>
      </FormPage>
    );
  }

  if (fetchState === "error" || !formCard) {
    const isFetchError = fetchState === "error";
    const message = isFetchError
      ? errorMessage
      : "No claim form is available for this benefit.";
    return (
      <FormPage>
        <ErrorContainer>
          <ErrorText>{message}</ErrorText>
          {isFetchError ? (
            <RetryButton type="button" onClick={retry}>
              Try Again
            </RetryButton>
          ) : null}
          <BackButton type="button" onClick={onBack}>
            <BackIconWrapper aria-hidden="true">
              <ChevronUpIcon color={theme.blueText} cursor="pointer" />
            </BackIconWrapper>
          </BackButton>
        </ErrorContainer>
      </FormPage>
    );
  }

  const requestCard = findFlowCard<ClaimRequestCardData>(
    flowData,
    CLAIM_FLOW_CARD.CLAIM_REQUEST
  );

  const benefitKey = flowData?.benefitKey ?? "";

  return (
    <FormPage>
      <FormContainer>
        <FormGrid>
          <LeftColumn>
            <ClaimForm
              formCard={formCard}
              bookingId={bookingId}
              partnerBookingId={partnerBookingId}
              currency={currency}
              benefitKey={benefitKey}
              onBack={onBack}
              onSubmitResult={setResult}
            />
          </LeftColumn>
          <RightColumn>
            <ClaimRequestSummary data={requestCard} />
          </RightColumn>
        </FormGrid>
      </FormContainer>
    </FormPage>
  );
}
